import React, { useEffect, createContext, useContext, useState, useCallback, } from 'react';
import { useFirebase } from './firebase-context';
import { SESSION_DURATION_S, IDLE_TIMEOUT_DURATION_S, } from '../constants/settings';
import { useIdleTimer } from 'react-idle-timer';
const LAST_ACTIVE_LS_KEY = 'last_active';
export const AuthContext = createContext({
    firebaseUser: null,
    isLoggedIn: false,
    logout: () => new Promise(() => undefined),
    login: () => new Promise(() => undefined),
    loginSSO: () => new Promise(() => undefined),
    sessionExpired: false,
});
/**
 *
 * This provides basic authentication methods to the app and security functionality:
 * * Expiring a users session after they become idle
 * * Not allowing a user to continue a session if they have not reauthenticated
 *   within a period of time
 * * Handling this behavior across multiple browser tabs open.
 *
 * This provider must be wrapped with a {@link FirebaseProvider} to work.
 *
 * Use {@link useAuth} for accessing the context value.
 */
export const AuthProvider = ({ children }) => {
    const firebase = useFirebase();
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const logout = useCallback(async (sessionExpired = false) => {
        if (sessionExpired) {
            setSessionExpired(true);
        }
        const response = await firebase.doSignOut();
        return response;
    }, []);
    const login = useCallback(async (email, password) => {
        const response = await firebase.doSignInWithEmailAndPassword(email, password);
        if (!response.user) {
            throw Error('Login response failed');
        }
        // Reset session expired
        setSessionExpired(false);
        return response;
    }, []);
    const loginSSO = useCallback(async () => {
        return await firebase.doSignInWithSSO();
    }, []);
    /**
     * Auto log out user if they become idle for a period of time
     */
    const handleOnIdle = useCallback(() => {
        if (firebase.auth.currentUser === null ||
            firebase.auth.currentUser.isAnonymous) {
            return;
        }
        // Get the last usage time from local storage.
        // This allows us to check if another browser tab
        // was opened since we were idle. In this case we
        // don't log out since the other tab has activity.
        const lastActiveIso = localStorage.getItem(LAST_ACTIVE_LS_KEY);
        if (lastActiveIso) {
            const lastActive = new Date(lastActiveIso).getTime();
            const now = new Date().getTime();
            if (now - lastActive < (IDLE_TIMEOUT_DURATION_S - 1) * 1000) {
                // Resets the idle timer
                reset();
                return;
            }
        }
        logout(true);
    }, [firebase]);
    const handleOnAction = useCallback(() => {
        const lastActiveIso = new Date().toISOString();
        localStorage.setItem(LAST_ACTIVE_LS_KEY, lastActiveIso);
    }, []);
    const { reset } = useIdleTimer({
        timeout: IDLE_TIMEOUT_DURATION_S * 1000,
        onIdle: handleOnIdle,
        onAction: handleOnAction,
        throttle: 1000,
    });
    /**
     * Creates a handler for login / logout events
     */
    useEffect(() => {
        return firebase.onAuthStateChanged(async (user) => {
            if (user === null) {
                setFirebaseUser(null);
                await firebase.doSignInAnonymously();
            }
            else {
                // If user is already logged in when the they load the app
                if (!user.isAnonymous && !hasInitialized) {
                    // The user signed in more than SESSION_DURATION_S ago, so
                    // log them out
                    const tokenResult = await user.getIdTokenResult(false);
                    const authTime = new Date(tokenResult.authTime);
                    const expirationTime = new Date(authTime);
                    expirationTime.setMilliseconds(authTime.getMilliseconds() + SESSION_DURATION_S * 1000);
                    if (new Date() > expirationTime) {
                        return logout(true);
                    }
                }
                setFirebaseUser(user);
            }
            if (!hasInitialized) {
                setHasInitialized(true);
            }
        });
    }, [logout, hasInitialized]);
    const context = {
        firebaseUser,
        isLoggedIn: firebaseUser !== null && !firebaseUser.isAnonymous,
        logout,
        login,
        loginSSO,
        sessionExpired,
    };
    return (React.createElement(AuthContext.Provider, { value: context }, children));
};
/**
 * Get authentication methods and properties from
 * the context.
 *
 * The component that uses it must be wrapped in
 * a {@link AuthProvider}
 */
export const useAuth = () => {
    return useContext(AuthContext);
};
//# sourceMappingURL=auth-context.js.map